import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Chip,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@mui/material';
import {
  AccountCircle,
  Lock,
  Logout,
  Menu as MenuIcon,
} from '@mui/icons-material';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/hooks/useAuth';
import { routeConfig } from '@/routes';
import { useVariant } from '@/contexts/VariantContext';
import { UserAvatar } from '../common/UserAvatar.tsx';
import { NotificationBell } from '../notifications/NotificationBell';
import { NewsBell } from '../news/NewsBell';

interface TopBarProps {
  drawerWidth: number;
  onMobileMenuToggle: () => void;
  onChangePassword?: () => void;
  rightAction?: React.ReactNode;
  titleOverride?: string;
  backgroundColor?: string;
  textColor?: string;
}

export const TopBar: React.FC<TopBarProps> = ({
  drawerWidth,
  onMobileMenuToggle,
  onChangePassword,
  rightAction,
  titleOverride,
  backgroundColor,
  textColor,
}) => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const { variant, setVariant } = useVariant();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const isDashboard = location.pathname === '/dashboard';
  const userRoles = (user?.roles || []).map(r => r.toLowerCase());
  const isClient = userRoles.includes('client');

  const routeName = routeConfig.find((r) => r.path && matchPath({ path: r.path, end: true }, location.pathname))?.name;
  const pageTitle = titleOverride ?? (routeName ? t(routeName) : 'Everyday Studio');

  const userName = user?.name || user?.email || '';

  const handleClose = () => setAnchorEl(null);

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        width: { md: `calc(100% - ${drawerWidth}px)` },
        ml: { md: `${drawerWidth}px` },
        zIndex: (theme) => theme.zIndex.appBar, // Standard AppBar z-index
        backgroundColor: backgroundColor || 'primary.main',
        color: textColor || 'inherit',
        borderBottom: '1px solid rgba(0,0,0,0.08)',
        transition: 'background-color 0.3s ease, color 0.3s ease',
      }}
    >
      <Toolbar sx={{ minHeight: '52px !important' }}>
        <IconButton
          color="inherit"
          edge="start"
          onClick={onMobileMenuToggle}
          sx={{ mr: 2, display: { md: 'none' } }}
        >
          <MenuIcon />
        </IconButton>

        <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontWeight: 700, fontSize: '1.1rem' }}>
          {pageTitle}
        </Typography>

        {isDashboard && (
          <Box sx={{ display: 'flex', gap: 0.5, mr: 1 }}>
            <Chip
              label="A"
              size="small"
              onClick={() => setVariant('a')}
              sx={{
                bgcolor: variant === 'a' ? 'rgba(255,255,255,0.25)' : 'transparent',
                color: 'inherit',
                border: '1px solid rgba(255,255,255,0.3)',
                cursor: 'pointer',
                fontWeight: variant === 'a' ? 700 : 400,
                height: 24,
                '& .MuiChip-label': { px: 1 }
              }}
            />
            <Chip
              label="Б"
              size="small"
              onClick={() => setVariant('b')}
              sx={{
                bgcolor: variant === 'b' ? 'rgba(255,255,255,0.25)' : 'transparent',
                color: 'inherit',
                border: '1px solid rgba(255,255,255,0.3)',
                cursor: 'pointer',
                fontWeight: variant === 'b' ? 700 : 400,
                height: 24,
                '& .MuiChip-label': { px: 1 }
              }}
            />
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <NewsBell />
          <NotificationBell />

          {isClient && (
            <>
              <IconButton
                size="small"
                aria-controls="profile-menu"
                aria-haspopup="true"
                onClick={(e) => setAnchorEl(e.currentTarget)}
                color="inherit"
                sx={{ ml: 0.5 }}
              >
                <UserAvatar path={user?.avatar} name={userName} sx={{ width: 32, height: 32 }} />
              </IconButton>

              <Menu
                id="profile-menu"
                anchorEl={anchorEl}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                keepMounted
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <Box sx={{ px: 2, py: 1, maxWidth: 320 }}>
                  <Typography variant="subtitle2" noWrap fontWeight={700}>{user?.name || 'Користувач'}</Typography>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', mb: 1 }}>
                    {user?.email}
                  </Typography>
                  {user?.roles && (
                    <Box sx={{ mb: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {user.roles.map((role) => (
                        <Chip 
                          key={role} 
                          label={role} 
                          size="small" 
                          variant="outlined" 
                          sx={{ height: 18, fontSize: '10px' }} 
                        />
                      ))}
                    </Box>
                  )}
                </Box>
                <Divider />
                <MenuItem onClick={() => { handleClose(); navigate('/settings', { state: { activeTab: 'profile' } }); }}>
                  <ListItemIcon><AccountCircle fontSize="small" /></ListItemIcon>
                  <Typography variant="body2">Профіль</Typography>
                </MenuItem>
                <MenuItem onClick={() => { handleClose(); onChangePassword?.(); }}>
                  <ListItemIcon><Lock fontSize="small" /></ListItemIcon>
                  <Typography variant="body2">Змінити пароль</Typography>
                </MenuItem>
                <Divider />
                <MenuItem onClick={() => { handleClose(); logout(); }}>
                  <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
                  <Typography variant="body2">Вийти</Typography>
                </MenuItem>
              </Menu>
            </>
          )}

          {rightAction && (
            <Box sx={{ ml: 0.5, display: { md: 'none' } }}>
              {rightAction}
            </Box>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};
